import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
} from "@mui/material";
import { editBrokerTransaction } from "../../../Services/BrokerTransactionsApi";

/*
 Props:
 - open: boolean
 - onClose: () => void
 - transaction: object (the transaction to edit)
 - editingUserId: user id (optional) -- سيتم إرساله إلى الدالة
 - onSuccess: () => void
*/
const EditBrokerTransactionDialog = ({ open, onClose, transaction = null, user_id = null, initialUserPayload = null, onSuccess }) => {
  const [form, setForm] = useState({ type: "deposite", amount: "", date: "", note: "", image: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm({
      type: transaction?.type ?? "deposite",
      amount: transaction?.amount ?? "",
      date: transaction?.date ?? transaction?.created_at?.split("T")?.[0] ?? "",
      note: transaction?.note ?? "",
      image: null,
    });
  }, [open, transaction]);

  const handleChange = (key) => (e) => {
    const v = key === "image" ? (e.target.files?.[0] ?? null) : e.target.value;
    setForm((s) => ({ ...s, [key]: v }));
  };

  // broker_id للعرض داخل النموذج (أولوية للـ user_id الممرّر)
  const brokerIdToShow =
    user_id ??
    transaction?.transaction_broker?.id ??
    transaction?.broker_id ??
    transaction?.user?.id ??
    transaction?.user_id ??
    "";

  const submit = async () => {
    setError("");
    if (!transaction?.id) return setError("معرّف العملية غير متوفر");
    if (!form.amount) return setError("المبلغ مطلوب");
    if (!form.date) return setError("التاريخ مطلوب");
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken") || null;

      // إذا كان هناك ملف نستخدم FormData (multipart) ونضيف broker_id إن وُجد في الـ payload
      if (form.image) {
        const fd = new FormData();
        fd.append("type", form.type);
        fd.append("amount", form.amount);
        fd.append("date", form.date);
        // أضف مفتاح note دائماً (السيرفر يتوقع وجوده)
        fd.append("note", form.note ?? "");
        fd.append("image", form.image);
        if (brokerIdToShow) fd.append("broker_id", brokerIdToShow);
        await editBrokerTransaction(transaction.id, fd, token);
      } else {
        // خلاف ذلك نرسل JSON (الخادم قد يتوقع JSON)
        const payload = {
          type: form.type,
          amount: form.amount,
          date: form.date,
          // أضف note فارغاً إذا لم يُدخل المستخدم شيئاً
          note: form.note ?? "",
        };
        if (brokerIdToShow) payload.broker_id = brokerIdToShow;
        await editBrokerTransaction(transaction.id, payload, token);
      }

      onSuccess && onSuccess();
    } catch (err) {
      console.error("editBrokerTransaction error:", err);
      setError(err?.response?.data?.message || err.message || "فشل تعديل المعاملة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>تعديل المعاملة #{transaction?.id ?? ""}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Typography color="error">{error}</Typography>}
          {/* حقل عرض broker_id المأخوذ من initialUserPayload (إن وُجد) */}
          <TextField label="broker_id" value={brokerIdToShow} size="small" fullWidth InputProps={{ readOnly: true }} />
          <FormControl size="small" fullWidth>
            <InputLabel id="edit-type-label">النوع</InputLabel>
            <Select labelId="edit-type-label" value={form.type} label="النوع" onChange={handleChange("type")}>
              <MenuItem value="deposite">إيداع</MenuItem>
              <MenuItem value="withdraw">سحب</MenuItem>
            </Select>
          </FormControl>

          <TextField label="المبلغ" type="number" size="small" fullWidth value={form.amount} onChange={handleChange("amount")} inputProps={{ step: "0.01" }} />
          <TextField label="تاريخ العملية" type="date" size="small" fullWidth value={form.date} onChange={handleChange("date")} InputLabelProps={{ shrink: true }} />
          <TextField label="ملاحظة" multiline rows={3} size="small" fullWidth value={form.note} onChange={handleChange("note")} />
          <Button variant="outlined" component="label">
            رفع ملف (اختياري)
            <input hidden type="file" accept="image/*,application/pdf" onChange={handleChange("image")} />
          </Button>
          {form.image && <Typography variant="caption">{form.image.name}</Typography>}
          {/* عرض user_id إن وُجد للمراجعة */}
          {user_id && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              سيتم إرسال user_id: {String(user_id)}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>إلغاء</Button>
        <Button onClick={submit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={18} color="inherit" /> : "حفظ التعديل"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBrokerTransactionDialog;
